import React from 'react';
import { FixedTableHeader, TableHeader as StyledTableHeader } from '../../TableContent.styles';
import { Text } from '../../../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Icon } from '../../../../../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Tooltip } from '../../../../../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Box } from '../../../../../../../../../../../../CommonComponents/OPL/Box/Box';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '../../../../../../../../../../../../CommonComponents/OPL/Menu/Menu';
import { IconButton } from '../../../../../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../../../../../../../../../../CommonComponents/OPL/List/ListItem';
import { Badge } from '../../../../../../../../../../../../CommonComponents/OPL/Badge/Badge';
import {
  CollapsableHeadersData,
  FilteredCategoryAndHeaders,
  Header,
} from '../../../../FlowTable.meta';
import {
  Popover,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../../../../../../../../CommonComponents/OPL/Popover/Popover';

interface Props {
  filteredCategoriesAndHeaders: FilteredCategoryAndHeaders[];
  hasShowCollapsed(id: number): boolean;
  toggleShowCollapsed(id: number): void;
  collapsableHeadersData: CollapsableHeadersData;
}

export const TableHeader = ({
  toggleShowCollapsed,
  filteredCategoriesAndHeaders,
  hasShowCollapsed,
  collapsableHeadersData,
}: Props) => {
  const headers = filteredCategoriesAndHeaders.reduce((acc, category) => {
    return [...acc, ...category.filteredHeaders];
  }, [] as Header[]);

  return (
    <thead>
      <tr>
        {filteredCategoriesAndHeaders.map((category, index) => {
          const {
            id,
            name,
            additionalInfo,
            filteredHeaders,
            showObligationsExceedLiquidAssetsWarning,
          } = category;

          const size = filteredHeaders.length;

          if (size === 0) {
            return null;
          }

          if (index === 0) {
            return (
              <FixedTableHeader position={index} colSpan={2} key={id}>
                <Box display="flex" alignItems="center">
                  <Text
                    textAlign="left"
                    fontSize="small"
                    fontWeight={600}
                    textTransform="uppercase"
                  >
                    {name}
                  </Text>

                  {showObligationsExceedLiquidAssetsWarning && (
                    <Popover>
                      <PopoverTrigger>
                        <IconButton
                          aria-label="Submenu"
                          icon="far fa-exclamation-triangle"
                          variant="warning"
                          size="small"
                          ml="8"
                        />
                      </PopoverTrigger>

                      <PopoverContent width={384} p="16">
                        <Text fontWeight={500} mb="16" textTransform="capitalize">
                          Estate Obligations Exceed Liquid Assets
                        </Text>

                        <Text fontSize="small" color="neutral.500">
                          The obligations of the estate, which include administration expenses, tax
                          liabilities or other outstanding liability balances exceed the available
                          balance of liquid assets
                        </Text>

                        <PopoverClose />

                        <PopoverArrow />
                      </PopoverContent>
                    </Popover>
                  )}
                </Box>
              </FixedTableHeader>
            );
          }

          return (
            <StyledTableHeader colSpan={size} hasDivider={index > 1} key={id}>
              <Text textAlign="left" fontSize="small" fontWeight={600} textTransform="uppercase">
                {name}
              </Text>

              {additionalInfo && (
                <Tooltip content={additionalInfo}>
                  <Icon
                    className="far fa-info-circle"
                    color="neutral.500"
                    fontSize="small"
                    ml="4"
                  />
                </Tooltip>
              )}
            </StyledTableHeader>
          );
        })}
      </tr>

      <tr>
        {headers.map((header, index) => {
          const { name, collapsed, id, additionalInfo } = header;

          const canCollapse =
            Object.keys(collapsableHeadersData).length !== 0 &&
            Object.keys(collapsableHeadersData.header).includes(id.toString());

          if (hasShowCollapsed(header.id) && canCollapse ? !collapsed : collapsed) {
            return null;
          }

          const isCollapsed = canCollapse
            ? collapsableHeadersData.header[id].collapsedData.name === header.name
            : false;

          if (index <= 1) {
            return (
              <FixedTableHeader key={index} position={index} aria-label="Fixed Header">
                <Text textAlign={index === 0 ? 'left' : 'right'} variant="smallCaps">
                  {name}

                  {additionalInfo && (
                    <Tooltip content={additionalInfo}>
                      <Icon ml="4" className="fas fa-exclamation-triangle" color="warning.400" />
                    </Tooltip>
                  )}
                </Text>
              </FixedTableHeader>
            );
          }

          const hasDivider =
            headers[index - 1] && headers[index - 1].categoryId !== header.categoryId;

          const showCollapsableMenu =
            canCollapse &&
            (index === headers.length - 1 ||
              (headers[index + 1] && headers[index + 1].id !== header.id));

          return (
            <StyledTableHeader key={index} hasDivider={hasDivider}>
              <Box display="flex" alignItems="center">
                <TruncatedText textAlign="right" variant="smallCaps" width="100%">
                  {name}
                </TruncatedText>

                {additionalInfo && (
                  <Tooltip content={additionalInfo}>
                    <Icon ml="4" className="fas fa-exclamation-triangle" color="warning.400" />
                  </Tooltip>
                )}

                {isCollapsed ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="content"
                    flexDirection="column"
                    ml="8"
                  >
                    <Badge variant="primary" size="small">
                      {collapsableHeadersData.header[id].singleData.length}
                    </Badge>
                  </Box>
                ) : null}

                {(isCollapsed || showCollapsableMenu) && (
                  <Menu>
                    <MenuTrigger>
                      <IconButton
                        aria-label="Menu"
                        icon="fas fa-caret-down"
                        variant="secondary"
                        size="small"
                        ml="4"
                      />
                    </MenuTrigger>

                    <MenuContent width={300}>
                      <MenuItem onSelect={() => toggleShowCollapsed(header.id)}>
                        <ListItem icon="far fa-eye-slash">
                          {showCollapsableMenu ? 'Hide' : 'Show'} Individual Beneficiary Details
                        </ListItem>
                      </MenuItem>
                    </MenuContent>
                  </Menu>
                )}
              </Box>
            </StyledTableHeader>
          );
        })}
      </tr>
    </thead>
  );
};
